import type {
  AccessContext,
  iUserTokenAccess,
  StoredUserAccess,
  TaDeskCapability,
} from "./access.models";
import { isValidUserAccess } from "./access.models";
import {
  normalizeStoredUserAccess,
  normalizeTokenAccess,
} from "./access-v1-adapter";

export interface ResolveAccessContextInput {
  userId: string;
  organizationId: string;
  access?: StoredUserAccess | null;
  tokenAccess?: iUserTokenAccess | null;
}

function buildAccessContext(
  userId: string,
  organizationId: string,
  normalized: {
    isOwner: boolean;
    capabilities: TaDeskCapability[];
    brandIds: string[];
  }
): AccessContext {
  return {
    userId,
    organizationId,
    isOwner: normalized.isOwner,
    capabilities: new Set(normalized.capabilities),
    accessibleBrandIds: normalized.isOwner ? [] : [...normalized.brandIds],
  };
}

export function resolveAccessContext(
  input: ResolveAccessContextInput
): AccessContext {
  const { userId, organizationId, access, tokenAccess } = input;

  if (access && isValidUserAccess(access)) {
    return buildAccessContext(
      userId,
      organizationId,
      normalizeStoredUserAccess(access)
    );
  }

  if (tokenAccess) {
    return buildAccessContext(
      userId,
      organizationId,
      normalizeTokenAccess(tokenAccess)
    );
  }

  return emptyAccessContext(userId, organizationId);
}

export function emptyAccessContext(
  userId: string,
  organizationId: string
): AccessContext {
  return {
    userId,
    organizationId,
    isOwner: false,
    capabilities: new Set(),
    accessibleBrandIds: [],
  };
}

/** Org-wide capability check — brand assignment does not affect capability presence. */
export function hasCapability(
  ctx: AccessContext,
  capability: TaDeskCapability
): boolean {
  if (ctx.isOwner) return true;
  return ctx.capabilities.has(capability);
}

export function hasAnyCapability(
  ctx: AccessContext,
  capabilities: TaDeskCapability[]
): boolean {
  return capabilities.some((cap) => hasCapability(ctx, cap));
}

export function canAccessBrand(ctx: AccessContext, brandId: string): boolean {
  if (ctx.isOwner) return true;
  return ctx.accessibleBrandIds.includes(brandId);
}

/** Org-wide accounting view, or brand assignment. Used for read access to CRM records. */
export function canViewCrmEntityBrand(
  ctx: AccessContext,
  brandId: string | undefined | null
): boolean {
  if (!brandId) return true;
  if (ctx.isOwner) return true;
  if (ctx.capabilities.has("VIEW_ACCOUNTING")) return true;
  return ctx.accessibleBrandIds.includes(brandId);
}

export function hasOrgWideAccountingView(ctx: AccessContext): boolean {
  return ctx.isOwner || ctx.capabilities.has("VIEW_ACCOUNTING");
}

export function hasOrgWideReportsView(ctx: AccessContext): boolean {
  return ctx.isOwner || hasCapability(ctx, "VIEW_REPORTS");
}

/** Any assigned brand, org-wide accounting view, or org-wide reports view. */
export function hasBrandCrmViewAccess(ctx: AccessContext): boolean {
  if (ctx.isOwner) return true;
  if (hasOrgWideAccountingView(ctx)) return true;
  if (hasOrgWideReportsView(ctx)) return true;
  return ctx.accessibleBrandIds.length > 0;
}
